import { useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ColumnConditionValue } from '@makyoapp/makyo-common'

import { DEFAULT_TABLE_PAGINATION, IDBTable, TablePagination } from '@/core/db'
import { TableFormattedData } from '@/core/db/table'
import { IRow } from '@/core/models/tables/row'
import { queryKeys } from '@/lib/react-query'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useCompContext } from '@/context/__project__/CompContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { getSafely } from '@/shared/safely'

//--------------------------------------------------------------------------------------//
//                                  Table Data Hook                                     //
//--------------------------------------------------------------------------------------//

const initialData: TableFormattedData = {
  rows: [] as IRow[],
  filteredRowsCount: 0,
  allRowsCount: 0,
}

interface CompTableDataParams {
  table: IDBTable | undefined
  componentId?: string
  pagination?: TablePagination
  filterSetting?: ColumnConditionValue
  sortSetting?: { sortType: string; sortBy: string }
  search?: string
  userFilter?: Record<string, any>
}

/**
 * !! DO NOT TOUCH !! - this is the final implementation of useCompTableData!
 *
 * Main hook to access table data (rows) within component.
 *
 * @note if the component has pagination or filter, then the componentId is required
 */
export const useCompTableData = ({
  table,
  componentId,
  pagination = DEFAULT_TABLE_PAGINATION,
  filterSetting,
  sortSetting,
  search,
  userFilter,
}: CompTableDataParams) => {
  // get table id
  const tableId = table?.id

  // access global contexts
  const { dbWorker, language, userSpecific, loadedTables } = useDbClientContext()
  const { userEmail } = useAppAuthContext()
  const { detailTableId, detailRowIndex, rowId: detailRowId } = useCompContext()
  const queryClient = useQueryClient()

  // this act as both loading state and query dependency
  const tableStatus = !!tableId && loadedTables.has(tableId) ? 'READY' : 'PENDING'

  // create memoized params for table data fetching
  const memoizedParams = useMemo(() => {
    return [
      language,
      userSpecific,
      pagination,
      generateFilter(filterSetting, userEmail, tableId, detailTableId, detailRowIndex, detailRowId),
      generateSort(sortSetting),
      search,
      userFilter,
      componentId,
    ] as const
  }, [
    detailRowIndex,
    detailTableId,
    filterSetting,
    language,
    pagination,
    search,
    sortSetting,
    tableId,
    userEmail,
    userFilter,
    userSpecific,
    componentId,
    detailRowId,
  ])

  // create a stable query key based on the current params
  const stableQueryKey = useMemo(() => {
    const [language, userSpecific, pagination, filter, sort, search, userFilter, componentId] = memoizedParams

    return [
      queryKeys.compTableData,
      `t:${tableId}`,
      `c:${componentId}`,
      `ln:${language}`,
      `us:${userSpecific}`,
      `f:${JSON.stringify(filter ?? {})}`,
      `uf:${JSON.stringify(userFilter ?? {})}`,
      `q:${search ?? ''}`,
      `st: ${JSON.stringify(sort ?? {})}`,
      `p:${pagination.page}:${pagination.perPage}`,
      `ts:${tableStatus}`,
      `dt:${detailTableId ?? ''}`,
      `dr:${detailRowIndex ?? ''}`,
      `drId:${detailRowId ?? ''}`,
    ]
    //
  }, [memoizedParams, tableId, tableStatus, detailTableId, detailRowIndex, detailRowId])

  /**
   * initialize query
   */
  const dataQuery = useQuery({
    queryKey: stableQueryKey,
    queryFn: async () => {
      if (!table) return
      return table.GetFormattedData(...memoizedParams)
    },
    enabled: !!table && tableStatus === 'READY',
    staleTime: Infinity, // keep data fresh to prevent additional fetch
  })

  // derived loading states
  const isLoadingData = dataQuery.isLoading
  const isLoading = tableStatus === 'PENDING' || isLoadingData

  /**
   * request table data from worker
   * @dbWorker
   */
  useEffect(() => {
    if (!dbWorker || !tableId) return

    const fetchData = () => {
      dbWorker.sendAction({
        type: 'get_table_data',
        payload: {
          table_id: tableId,
          language,
        },
        meta: { from: 'useCompTableData' },
      })
    }

    fetchData()

    // re-fetch table data on window focus or tab change
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchData()
      }
    }

    // window.addEventListener('focus', handleVisibilityChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      // window.removeEventListener('focus', handleVisibilityChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [dbWorker, language, tableId, loadedTables])

  /**
   * subscribe to table data changes from db-worker
   */
  useEffect(() => {
    if (!table) return

    const unsubscribe = table.subscribe(() => {
      queryClient.invalidateQueries({ queryKey: stableQueryKey })
    })

    return () => {
      unsubscribe()
    }
  }, [stableQueryKey, queryClient, table])

  return [
    getSafely(dataQuery, ['data'], initialData), // formatted data result
    isLoading,
  ] as const
}

//--------------------------------------------------------------------------------------//
//                                  Helper Functions                                    //
//--------------------------------------------------------------------------------------//

function generateFilter(
  filterSetting: ColumnConditionValue | undefined,
  userEmail: string,
  tableId: string | undefined,
  detailTableId: string | undefined,
  detailRowIndex: number | undefined,
  detailRowId: string | undefined
) {
  if (!filterSetting || !filterSetting.conditions.length) return

  return {
    settings: filterSetting,
    userEmail,
    tableId,
    detailTableId,
    detailRowIndex,
    detailRowId,
  }
}

function generateSort(sortSetting: { sortType: string; sortBy: string } | undefined) {
  if (!sortSetting || !sortSetting.sortType) return

  return {
    sortType: sortSetting.sortType || 'asc',
    sortBy: sortSetting.sortBy,
  }
}
